import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Progress } from './ui/progress';
import { Trophy, FileBarChart, TrendingUp, ChevronRight, Sparkles, Calendar, Flame, Target, Zap, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MesocycleReadyModal = ({ onShowReport }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mesocycleData, setMesocycleData] = useState(null);

  useEffect(() => {
    if (user && user.role === 'client') {
      checkMesocycleStatus();
    }
  }, [user]);

  const checkMesocycleStatus = async () => {
    try {
      const response = await axios.get(`${API}/me/mesocycle-full-report`);
      const data = response.data;
      
      // Mostrar modal si ha completado >= 80% del mesociclo (4 de 5 semanas)
      if (data && (data.progress_percent >= 80 || data.can_finalize)) {
        // Verificar si ya fue mostrado en esta sesión
        const dismissedKey = `mesocycle_modal_${user.id}_meso${data.mesocycle}_week${data.current_week}`;
        const wasDismissed = sessionStorage.getItem(dismissedKey);
        
        if (!wasDismissed) {
          setMesocycleData(data);
          setOpen(true);
        }
      }
    } catch (error) {
      console.error('Error checking mesocycle status:', error);
    }
  };

  const handleViewReport = () => {
    dismissModal();
    if (onShowReport) {
      onShowReport();
    }
  };

  const dismissModal = () => {
    if (mesocycleData) {
      const dismissedKey = `mesocycle_modal_${user.id}_meso${mesocycleData.mesocycle}_week${mesocycleData.current_week}`;
      sessionStorage.setItem(dismissedKey, 'true');
    }
    setOpen(false);
  };

  if (!open || !mesocycleData) return null;

  const isWeek4 = mesocycleData.current_week === 4;
  const isWeek5 = mesocycleData.current_week === 5 || mesocycleData.can_finalize;
  const nextMeso = mesocycleData.mesocycle + 1;

  // Mensajes motivacionales según la semana
  const motivationalMessages = {
    week4: {
      title: "¡Estás en racha!",
      subtitle: `Semana 4 de 5 completada`,
      message: "Tu cuerpo está adaptándose. Una semana más y habrás completado tu primer ciclo de transformación.",
      cta: "Ver mi progreso",
      highlight: "¡Última semana por delante!"
    },
    week5: {
      title: "¡MESOCICLO COMPLETADO!",
      subtitle: `Has conquistado el Mesociclo ${mesocycleData.mesocycle}`,
      message: `Tu dedicación ha dado frutos. Es hora de ver tu informe completo y prepararte para el Mesociclo ${nextMeso}.`,
      cta: "Ver informe completo",
      highlight: `¡Bienvenido al Mesociclo ${nextMeso}!`
    }
  };

  const content = isWeek5 ? motivationalMessages.week5 : motivationalMessages.week4;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
      <div className="relative max-w-md w-full bg-gradient-to-br from-zinc-900 via-zinc-900 to-primary/20 border border-primary/40 rounded-2xl overflow-hidden shadow-2xl">
        
        {/* Animated background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative p-6">
          {/* Header with icon */}
          <div className="text-center mb-6">
            <div className="relative inline-block mb-4">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-primary/40 rounded-full blur-xl animate-pulse" />
              <div className={`relative p-4 rounded-full border-2 ${isWeek5 ? 'bg-gradient-to-br from-amber-500 to-primary border-amber-400' : 'bg-gradient-to-br from-primary/40 to-amber-500/40 border-primary/60'}`}>
                {isWeek5 ? (
                  <Trophy className="h-10 w-10 text-black" />
                ) : (
                  <Flame className="h-10 w-10 text-primary" />
                )}
              </div>
              {/* Sparkles */}
              <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-amber-400 animate-bounce" />
              <Star className="absolute -bottom-1 -left-1 h-4 w-4 text-primary animate-pulse" />
            </div>
            
            <h2 className={`text-2xl font-bold mb-1 ${isWeek5 ? 'text-amber-400' : 'text-foreground'}`}>
              {content.title}
            </h2>
            <p className="text-muted-foreground">
              {content.subtitle}
            </p>
          </div>

          {/* Progress section */}
          <div className="bg-zinc-800/60 rounded-xl p-4 mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Progreso del mesociclo</span>
              <span className="font-bold text-primary">{mesocycleData.progress_percent}%</span>
            </div>
            <Progress 
              value={mesocycleData.progress_percent} 
              className="h-3 bg-zinc-700"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>Semana {mesocycleData.current_week} de 5</span>
              <span>{mesocycleData.sessions_completed || 0} sesiones</span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-zinc-800/40 rounded-lg p-3 text-center">
              <Calendar className="h-5 w-5 mx-auto mb-1 text-blue-400" />
              <p className="text-lg font-bold text-foreground">{mesocycleData.current_week}</p>
              <p className="text-xs text-muted-foreground">Semana</p>
            </div>
            <div className="bg-zinc-800/40 rounded-lg p-3 text-center">
              <TrendingUp className="h-5 w-5 mx-auto mb-1 text-emerald-400" />
              <p className="text-lg font-bold text-foreground">{mesocycleData.mesocycle}</p>
              <p className="text-xs text-muted-foreground">Mesociclo</p>
            </div>
            <div className="bg-zinc-800/40 rounded-lg p-3 text-center">
              <Zap className="h-5 w-5 mx-auto mb-1 text-amber-400" />
              <p className="text-lg font-bold text-foreground">{mesocycleData.sessions_completed || 0}</p>
              <p className="text-xs text-muted-foreground">Sesiones</p>
            </div>
          </div>

          {/* Motivational message */}
          <div className={`p-4 rounded-xl mb-4 ${isWeek5 ? 'bg-amber-500/20 border border-amber-500/40' : 'bg-primary/10 border border-primary/30'}`}>
            <div className="flex items-start gap-3">
              <Target className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isWeek5 ? 'text-amber-400' : 'text-primary'}`} />
              <div>
                <p className={`text-sm font-semibold mb-1 ${isWeek5 ? 'text-amber-400' : 'text-foreground'}`}>
                  {content.highlight}
                </p>
                <p className="text-xs text-muted-foreground">
                  {content.message}
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleViewReport}
              className={`w-full py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                isWeek5 
                  ? 'bg-gradient-to-r from-amber-500 to-primary text-black hover:from-amber-400 hover:to-primary/90 shadow-lg shadow-amber-500/25' 
                  : 'bg-primary text-black hover:bg-primary/90'
              }`}
            >
              <FileBarChart className="h-5 w-5" />
              {content.cta}
              <ChevronRight className="h-5 w-5" />
            </button>

            <button
              onClick={dismissModal}
              className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Ver más tarde
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MesocycleReadyModal;
